// hooks/useRestoreFilteredProjects.ts
import { useEffect, useRef, useCallback } from 'react';
// import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useFiltersStore } from '@/store/useFiltersStore';
import { usePageStore } from '@/store/usePageStore';
import { scrollToElement } from '@/utils/generic';
import { IFilter, IFiltersGroup, IProjectFilteredList, ITabsAndFilterGroups } from '../FilteredProjectsGallery.types';
import { TProject } from '@/types/global';
import { IProjectsIDsResponse } from '@/directus/services/common/projects-ids/fetch';
import { useSearchParams } from 'next/dist/client/components/navigation';
import { useIsMobile } from '@/hooks/useIsMobile';

type Params = {
  hasHydrated: boolean;
  isPrivatePresentation: boolean;
  memoizedInitialProjList: IProjectFilteredList[];
  bufferedProjects: TProject[];
  initialProjectsIDs: IProjectsIDsResponse | undefined;
  filtersData: ITabsAndFilterGroups | undefined;
  fadeOutProjectsList: () => void;
  fadeInProjectsList: () => void;
  updateInitialFetchDisplayedProjects: () => void;
  fetchProjectsOrLoadInitialList: () => void;
  setFirstFetchProjects: (lists: IProjectFilteredList[]) => void;
  setLazyLoadedProjectsLists: (lists: IProjectFilteredList[]) => void;
  shouldRestoreState: () => boolean;
};

export function useInitialOrRestoreProjects({
  hasHydrated,
  isPrivatePresentation,
  memoizedInitialProjList,
  bufferedProjects,
  initialProjectsIDs,
  filtersData,
  fadeOutProjectsList,
  fadeInProjectsList,
  updateInitialFetchDisplayedProjects,
  fetchProjectsOrLoadInitialList,
  setFirstFetchProjects,
  setLazyLoadedProjectsLists,
  shouldRestoreState,
}: Params) {
  // refs to avoid re-running effect due to array/object identity changes
  const initialListsRef = useRef(memoizedInitialProjList);
  const bufferedRef = useRef(bufferedProjects);
  const idsRef = useRef(initialProjectsIDs);
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const setArchivedFilteredProjects = useFiltersStore((s) => s.setArchivedFilteredProjects);

  useEffect(() => { initialListsRef.current = memoizedInitialProjList; }, [memoizedInitialProjList]);
  useEffect(() => { bufferedRef.current = bufferedProjects; }, [bufferedProjects]);
  useEffect(() => { idsRef.current = initialProjectsIDs; }, [initialProjectsIDs]);

  const restoreScrollToPrevLink = useCallback(() => {
    const startTime = Date.now();
    const id = setInterval(() => {
      const elLink = document.getElementById(useFiltersStore.getState().previousClickedLinkProject || '');
      scrollToElement({ target: elLink, smooth: false, scrollToTop: true });
      if (elLink || Date.now() - startTime >= 2000) clearInterval(id);
    }, 100);
    return id;
  }, []);

    function findFilterByText(
    filterGroupsFromPage: IFiltersGroup[],
    searchText: string
  ): IFilter | undefined {
    const lowerSearch = searchText.toLowerCase();

    for (const group of filterGroupsFromPage) {
      for (const filter of group.filters) {
        // Check filter match
        if (filter.text.toLowerCase() === lowerSearch) {
          return filter;
        }

        // Check subfilters match
        if (filter.subfilters) {
          for (const subfilter of filter.subfilters) {
            if (subfilter.text.toLowerCase() === lowerSearch) {
              // Promote the subfilter to IFilter shape (without subfilters)
              return {
                ...subfilter,
                subfilters: [],
              };
            }
          }
        }
      }
    }

    // If no match found, return undefined
    return undefined;
  }

  const getInitialFiltersFromUrl = useCallback((): IFilter[] => {
    const filter = searchParams.get('filters');
    const filtersAddedWithInitial: IFilter[] = [];

    // For each filter in the URL, find the filter object in the filters data
    if (filter && filtersData) {
      filter.split(';').forEach((filter) => {
        // rollback format from url:
        const filterNoFormat = filter.replace(/-/g, ' ').toLowerCase();
        const filterFromData = findFilterByText(
          filtersData?.sections ?? [],
          filterNoFormat
        );
        if (filterFromData) {
          filtersAddedWithInitial.push(filterFromData);
        }
      });
    }

    return filtersAddedWithInitial;
  }, [searchParams, filtersData]);

  useEffect(() => {
    if (!hasHydrated) return;

    // 1) Private presentation: set projects and scroll, with cleanup
    if (isPrivatePresentation) {
      useFiltersStore.getState().setPresentationProjects(initialListsRef.current[0]?.projects || []);
      const id = restoreScrollToPrevLink();
      return () => clearInterval(id); 
    }

    const prevPageState = useFiltersStore.getState().savedFilteredProjectsState;
    const filtersFromUrl = getInitialFiltersFromUrl();

    // 2) Coming from previous work page
    if (usePageStore.getState().isPreviousPageWork) {
      const hadFilters =
        (prevPageState?.selectedFilters && prevPageState?.selectedFilters?.length > 0) ||
        prevPageState?.search?.value ||
        prevPageState?.imageSearch?.value;

      if (hadFilters) {
        useFiltersStore.getState().restoreOnlyFiltersState();

        // smooth scroll to filters (cleanup safe)
        const elFiltersContainer = !isMobile 
          ? document.getElementById('filters-container') 
          : document.getElementById('filters-heading-mobile');

        let count = 0;
        const interval = setInterval(() => {
          scrollToElement({ target: elFiltersContainer, scrollToTop: true, smooth: false });
          if (++count >= 4 || elFiltersContainer) clearInterval(interval);
        }, 100);


        return () => clearInterval(interval);
      } else {
        // no filters → initial fetch or load server list
        fetchProjectsOrLoadInitialList();
        return;
      }
    }

    // 3) Normal entry with no restoration needed
    if (!shouldRestoreState()) {
      // If there are filters in the URL, apply them and fetch projects
      if (filtersFromUrl.length > 0) {
        useFiltersStore.setState({ selectedFilters: { filters: filtersFromUrl, triggerer: 'ui' } });
        fetchProjectsOrLoadInitialList();
        useFiltersStore.setState({ areFiltersOpen: false})
        return;
      } else {
        fadeOutProjectsList();
        setFirstFetchProjects(initialListsRef.current);
        useFiltersStore.setState({
          nextBufferLazyLoadedProjects: bufferedRef.current,
          projectsIDs: idsRef.current,
        });
        fadeInProjectsList();
        updateInitialFetchDisplayedProjects();
        return;
      }
    }

    // 4) Restore saved state and scroll to previous link (with cleanup)
    useFiltersStore.setState({ isRestoringState: true });
    const wasLazyLoadFinished = prevPageState?.isProjectsLazyLoadFinished.isFinished;

    if (wasLazyLoadFinished) {
      setArchivedFilteredProjects(prevPageState?.archivedFilteredProjects || []);
      useFiltersStore.getState().setIsArchivedProjFetchFinished(true);
    }
    useFiltersStore.getState().restoreSavedFilteredProjectsState();

    setFirstFetchProjects(prevPageState?.firstFetchFilteredProjects || []);
    setLazyLoadedProjectsLists(prevPageState?.lazyLoadedProjectsLists || []);
    updateInitialFetchDisplayedProjects();

    const id = restoreScrollToPrevLink();
    return () => clearInterval(id);
  // Only update when hasHydrated changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated]);
}